import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, BookOpen, TrendingUp, DollarSign, Plus, Play, Gamepad2, CalendarDays, FileQuestion, Settings, Download, FileText, IndianRupee, X, Bell } from 'lucide-react';
import { toast } from 'sonner';
import SimulationStudio from './admin/SimulationStudio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user: currentUser, logout, fetchUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [caseRequests, setCaseRequests] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [newIntervention, setNewIntervention] = useState({ name: '', description: '' });
  const [pages, setPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [pageContent, setPageContent] = useState('');
  const [researchArticles, setResearchArticles] = useState([]);
  const [newArticle, setNewArticle] = useState({ title: '', author: '', content: '', product_type: '', price: 0 });
  const [editingArticle, setEditingArticle] = useState(null);
  const [pricing, setPricing] = useState({ three_months: 15000, six_months: 25000, twelve_months: 40000, intl_three_months: 250, intl_six_months: 450, intl_twelve_months: 720, corp_inr_six_months: 85000, corp_inr_twelve_months: 150000, corp_usd_six_months: 1500, corp_usd_twelve_months: 2500, academic_inr_per_id: 500, academic_usd_per_id: 10 });
  const [accessIds, setAccessIds] = useState([]);
  const [newAccessIdForm, setNewAccessIdForm] = useState({ institute_name: '', institute_email: '', category: 'student', quantity: 1 });
  const [generatingIds, setGeneratingIds] = useState(false);
  const [accessIdFilter, setAccessIdFilter] = useState({ institute: 'all', category: 'all', status: 'all' });
  const [savingPricing, setSavingPricing] = useState(false);
  const [financeOverview, setFinanceOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [txnStartDate, setTxnStartDate] = useState('');
  const [txnEndDate, setTxnEndDate] = useState('');
  const [txnStatusFilter, setTxnStatusFilter] = useState('all');
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [showAdminNotifications, setShowAdminNotifications] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const notifDropdownRef = useRef(null);
  const [subscribers, setSubscribers] = useState([]);
  const [newSubscriber, setNewSubscriber] = useState({ name: '', category: 'Academic', logo_url: '', display_order: 0, active: true });
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [savingSubscriber, setSavingSubscriber] = useState(false);
  // Account settings (change email / password)
  const [accountEmailForm, setAccountEmailForm] = useState({ current_password: '', new_email: '' });
  const [accountPwdForm, setAccountPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showAddSimulation, setShowAddSimulation] = useState(false);
  const [newCase, setNewCase] = useState({
    title: '',
    description: '',
    domain: [],
    sector: '',
    geography: 'India',
    year: new Date().getFullYear(),
    concept: '',
    level: [],
    length_pages: '',
    keywords: '',
    product_type: [],
    is_sample: false,
    author: ''
  });
  const [caseFile, setCaseFile] = useState(null);
  const [teachingNoteFile, setTeachingNoteFile] = useState(null);
  const [worksheetFile, setWorksheetFile] = useState(null);
  const [newVideo, setNewVideo] = useState({
    title: '',
    youtube_url: '',
    description: '',
    related_case_ids: []
  });
  const [newSimulation, setNewSimulation] = useState({
    title: '',
    description: '',
    simulation_url: '',
    thumbnail_url: '',
    domain: '',
    difficulty: 'Intermediate',
    duration: ''
  });

  useEffect(() => {
    fetchStats();
    fetchCases();
    fetchUsers();
    fetchVideos();
    fetchSimulations();
    fetchTrainingSessions();
    fetchCaseRequests();
    fetchInterventions();
    fetchPages();
    fetchResearchArticles();
    fetchPricing();
    fetchFinanceOverview();
    fetchTransactions();
    fetchActiveSubscriptions();
    fetchUnreadNotifCount();
    fetchSubscribers();
    fetchAccessIds();
    const notifInterval = setInterval(fetchUnreadNotifCount, 30000);
    return () => clearInterval(notifInterval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target)) {
        setShowAdminNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadNotifCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/unread-count`);
      setUnreadNotifCount(res.data.count);
    } catch {}
  };

  const fetchAdminNotifications = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications`);
      setAdminNotifications(res.data);
    } catch {}
  };

  const toggleAdminNotifications = async () => {
    if (!showAdminNotifications) await fetchAdminNotifications();
    setShowAdminNotifications(!showAdminNotifications);
  };

  const markAllNotifRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`);
      setUnreadNotifCount(0);
      setAdminNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers`);
      setSubscribers(res.data || []);
    } catch {
      toast.error('Failed to load subscribers');
    }
  };

  const fetchAccessIds = async () => {
    try {
      const res = await axios.get(`${API}/admin/access-ids`);
      setAccessIds(res.data || []);
    } catch {
      // Silent: admins without perms would already hit toast on tab open
    }
  };

  const handleGenerateAccessIds = async () => {
    const f = newAccessIdForm;
    if (!f.institute_name.trim()) { toast.error('Institute name is required'); return; }
    if (f.quantity < 1) { toast.error('Quantity must be at least 1'); return; }
    setGeneratingIds(true);
    try {
      const res = await axios.post(`${API}/admin/access-ids/generate`, {
        institute_name: f.institute_name.trim(),
        institute_email: f.institute_email.trim().toLowerCase(),
        category: f.category,
        quantity: parseInt(f.quantity, 10),
      });
      toast.success(`Generated ${res.data.count} ${f.category} ID(s) for ${f.institute_name}` + (f.institute_email ? '. Emailed to institute.' : '.'));
      setNewAccessIdForm({ institute_name: '', institute_email: '', category: 'student', quantity: 1 });
      await fetchAccessIds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate access IDs');
    } finally {
      setGeneratingIds(false);
    }
  };

  const handleDeleteAccessId = async (id, code) => {
    if (!window.confirm(`Delete access ID ${code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/access-ids/${id}`);
      toast.success(`Deleted ${code}`);
      await fetchAccessIds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddSubscriber = async () => {
    const name = (newSubscriber.name || '').trim();
    if (!name) {
      toast.error('Subscriber name is required');
      return;
    }
    setSavingSubscriber(true);
    try {
      await axios.post(`${API}/admin/subscribers`, {
        name,
        category: newSubscriber.category,
        logo_url: (newSubscriber.logo_url || '').trim(),
        display_order: Number(newSubscriber.display_order) || 0,
        active: newSubscriber.active,
      });
      toast.success('Subscriber added');
      setNewSubscriber({ name: '', category: 'Academic', logo_url: '', display_order: 0, active: true });
      await fetchSubscribers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add subscriber');
    } finally {
      setSavingSubscriber(false);
    }
  };

  const handleUpdateSubscriber = async () => {
    if (!editingSubscriber) return;
    const name = (editingSubscriber.name || '').trim();
    if (!name) {
      toast.error('Subscriber name cannot be empty');
      return;
    }
    setSavingSubscriber(true);
    try {
      await axios.put(`${API}/admin/subscribers/${editingSubscriber.id}`, {
        name,
        category: editingSubscriber.category,
        logo_url: (editingSubscriber.logo_url || '').trim(),
        display_order: Number(editingSubscriber.display_order) || 0,
        active: editingSubscriber.active,
      });
      toast.success('Subscriber updated');
      setEditingSubscriber(null);
      await fetchSubscribers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscriber');
    } finally {
      setSavingSubscriber(false);
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`);
      toast.success('Subscriber deleted');
      await fetchSubscribers();
    } catch {
      toast.error('Failed to delete subscriber');
    }
  };

  const toggleSubscriberActive = async (sub) => {
    try {
      await axios.put(`${API}/admin/subscribers/${sub.id}`, { active: !sub.active });
      await fetchSubscribers();
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    const newEmail = accountEmailForm.new_email.trim().toLowerCase();
    if (!accountEmailForm.current_password || !newEmail) {
      toast.error('Please fill in both fields');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await axios.put(`${API}/auth/me/email`, {
        current_password: accountEmailForm.current_password,
        new_email: newEmail
      });
      toast.success(res.data.message || 'Email updated');
      setAccountEmailForm({ current_password: '', new_email: '' });
      if (fetchUser) await fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!accountPwdForm.current_password || !accountPwdForm.new_password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (accountPwdForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (accountPwdForm.new_password !== accountPwdForm.confirm_password) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setSavingPwd(true);
    try {
      const res = await axios.put(`${API}/auth/me/password`, {
        current_password: accountPwdForm.current_password,
        new_password: accountPwdForm.new_password
      });
      toast.success(res.data.message || 'Password updated');
      setAccountPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPwd(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    }
  };

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      setCases(response.data);
    } catch (error) {
      toast.error('Failed to load cases');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    }
  };

  const fetchSimulations = async () => {
    try {
      const response = await axios.get(`${API}/simulations`);
      setSimulations(response.data);
    } catch (error) {
      toast.error('Failed to load simulations');
    }
  };

  const fetchTrainingSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/training-sessions`);
      setTrainingSessions(response.data);
    } catch (error) {
      console.error('Failed to load training sessions');
    }
  };

  const fetchCaseRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/case-requests`);
      setCaseRequests(response.data);
    } catch (error) {
      console.error('Failed to load case requests');
    }
  };

  const fetchInterventions = async () => {
    try {
      const response = await axios.get(`${API}/interventions`);
      setInterventions(response.data);
    } catch (error) {
      console.error('Failed to load interventions');
    }
  };

  const handleUpdateTrainingStatus = async (sessionId, status) => {
    try {
      await axios.put(`${API}/admin/training-sessions/${sessionId}/status`, { status });
      toast.success(`Session status updated to ${status}`);
      fetchTrainingSessions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateCaseRequestStatus = async (requestId, status, adminNotes = '') => {
    try {
      await axios.put(`${API}/admin/case-requests/${requestId}/status`, { status, admin_notes: adminNotes });
      toast.success(`Request status updated to ${status}`);
      fetchCaseRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateIntervention = async (e) => {
    e.preventDefault();
    if (!newIntervention.name.trim()) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${API}/admin/interventions`, newIntervention);
      toast.success('Intervention created');
      setNewIntervention({ name: '', description: '' });
      fetchInterventions();
    } catch (error) {
      toast.error('Failed to create intervention');
    }
  };

  const handleDeleteIntervention = async (id) => {
    if (!window.confirm('Delete this intervention?')) return;
    try {
      await axios.delete(`${API}/admin/interventions/${id}`);
      toast.success('Intervention deleted');
      fetchInterventions();
    } catch (error) {
      toast.error('Failed to delete intervention');
    }
  };

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/admin/pages`);
      setPages(response.data);
    } catch (error) {
      console.error('Failed to load pages');
    }
  };

  const handleEditPage = (page) => {
    setEditingPage(page.slug);
    setPageContent(page.content || '');
  };

  const handleSavePage = async (slug) => {
    try {
      await axios.put(`${API}/admin/pages/${slug}`, { content: pageContent });
      toast.success('Page content updated successfully');
      setEditingPage(null);
      setPageContent('');
      fetchPages();
    } catch (error) {
      toast.error('Failed to update page');
    }
  };

  const fetchResearchArticles = async () => {
    try {
      const response = await axios.get(`${API}/research`);
      setResearchArticles(response.data);
    } catch (error) {
      console.error('Failed to load research articles');
    }
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    if (!newArticle.title.trim() || !newArticle.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    try {
      await axios.post(`${API}/admin/research`, {
        ...newArticle,
        author: newArticle.author || 'The Case Verse',
        price: parseFloat(newArticle.price) || 0
      });
      toast.success('Product added successfully');
      setNewArticle({ title: '', author: '', content: '', product_type: '', price: 0 });
      fetchResearchArticles();
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/admin/research/${id}`);
      toast.success('Article deleted');
      fetchResearchArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleEditArticle = (article) => {
    setEditingArticle({ ...article });
  };

  const handleSaveArticle = async () => {
    try {
      await axios.put(`${API}/admin/research/${editingArticle.id}`, {
        title: editingArticle.title,
        content: editingArticle.content,
        author: editingArticle.author,
        product_type: editingArticle.product_type,
        price: parseFloat(editingArticle.price) || 0
      });
      toast.success('Article updated successfully');
      setEditingArticle(null);
      fetchResearchArticles();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricing({
        three_months: response.data.three_months,
        six_months: response.data.six_months,
        twelve_months: response.data.twelve_months,
        intl_three_months: response.data.intl_three_months || 250,
        intl_six_months: response.data.intl_six_months || 450,
        intl_twelve_months: response.data.intl_twelve_months || 720,
        corp_inr_six_months: response.data.corp_inr_six_months || 85000,
        corp_inr_twelve_months: response.data.corp_inr_twelve_months || 150000,
        corp_usd_six_months: response.data.corp_usd_six_months || 1500,
        corp_usd_twelve_months: response.data.corp_usd_twelve_months || 2500,
        academic_inr_per_id: response.data.academic_inr_per_id ?? 500,
        academic_usd_per_id: response.data.academic_usd_per_id ?? 10
      });
    } catch (error) {
      console.error('Failed to load pricing');
    }
  };

  const handleSavePricing = async (e) => {
    e.preventDefault();
    setSavingPricing(true);
    try {
      await axios.put(`${API}/admin/pricing`, pricing);
      toast.success('Subscription pricing updated successfully');
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const fetchFinanceOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/finance/overview`);
      setFinanceOverview(res.data);
    } catch (error) {
      console.error('Failed to load finance overview');
    }
  };

  const fetchTransactions = async (startDate, endDate, status) => {
    try {
      const params = new URLSearchParams();
      if (startDate || txnStartDate) params.append('start_date', startDate || txnStartDate);
      if (endDate || txnEndDate) params.append('end_date', endDate || txnEndDate);
      if (status || txnStatusFilter) params.append('status', status || txnStatusFilter);
      const res = await axios.get(`${API}/admin/finance/transactions?${params}`);
      setTransactions(res.data);
    } catch (error) {
      console.error('Failed to load transactions');
    }
  };

  const fetchActiveSubscriptions = async () => {
    try {
      const res = await axios.get(`${API}/admin/finance/subscriptions`);
      setActiveSubscriptions(res.data);
    } catch (error) {
      console.error('Failed to load subscriptions');
    }
  };

  const handleFilterTransactions = () => {
    fetchTransactions(txnStartDate, txnEndDate, txnStatusFilter);
  };

  const handleExportCSV = (type) => {
    const params = new URLSearchParams();
    if (txnStartDate) params.append('start_date', txnStartDate);
    if (txnEndDate) params.append('end_date', txnEndDate);
    window.open(`${API}/admin/finance/export/${type}?${params}`, '_blank');
  };

  const handleViewInvoice = async (transactionId) => {
    try {
      const res = await axios.get(`${API}/admin/finance/invoice/${transactionId}`);
      setInvoiceModal(res.data);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    
    if (!caseFile) {
      toast.error('Please upload a case study file');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('title', newCase.title);
      formData.append('description', newCase.description);
      formData.append('domain', Array.isArray(newCase.domain) ? newCase.domain.join(',') : newCase.domain);
      formData.append('sector', newCase.sector);
      formData.append('geography', newCase.geography);
      formData.append('year', newCase.year);
      formData.append('concept', newCase.concept);
      formData.append('level', Array.isArray(newCase.level) ? newCase.level.join(',') : newCase.level);
      formData.append('length_pages', newCase.length_pages);
      formData.append('keywords', newCase.keywords);
      formData.append('product_type', Array.isArray(newCase.product_type) ? newCase.product_type.join(',') : newCase.product_type);
      formData.append('author', newCase.author);
      formData.append('is_sample', newCase.is_sample);
      formData.append('case_file', caseFile);
      
      if (teachingNoteFile) {
        formData.append('teaching_note_file', teachingNoteFile);
      }
      
      if (worksheetFile) {
        formData.append('worksheet_file', worksheetFile);
      }
      
      await axios.post(`${API}/admin/cases/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Case uploaded successfully');
      setShowAddCase(false);
      setCaseFile(null);
      setTeachingNoteFile(null);
      setWorksheetFile(null);
      setNewCase({
        title: '',
        description: '',
        domain: [],
        sector: '',
        geography: 'India',
        year: new Date().getFullYear(),
        concept: '',
        level: 'MBA',
        length_pages: '',
        keywords: '',
        product_type: [],
        is_sample: false,
        author: ''
      });
      fetchCases();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload case');
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm('Are you sure you want to delete this case?')) return;
    
    try {
      await axios.delete(`${API}/admin/cases/${caseId}`);
      toast.success('Case deleted successfully');
      fetchCases();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete case');
    }
  };

  const handleReuploadCase = async (caseId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('case_file', file);
    try {
      await axios.post(`${API}/admin/cases/${caseId}/reupload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      toast.success('Content re-uploaded successfully with formatting preserved');
      fetchCases();
    } catch (error) {
      toast.error('Failed to re-upload content');
    }
  };


  const handleCreateVideo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/videos`, newVideo);
      toast.success('Video added successfully');
      setShowAddVideo(false);
      setNewVideo({ title: '', youtube_url: '', description: '', related_case_ids: [] });
      fetchVideos();
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`);
      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleCreateSimulation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/simulations`, newSimulation);
      toast.success('Simulation added successfully');
      setShowAddSimulation(false);
      setNewSimulation({ 
        title: '', 
        description: '', 
        simulation_url: '', 
        thumbnail_url: '',
        domain: '',
        difficulty: 'Intermediate',
        duration: ''
      });
      fetchSimulations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to add simulation');
    }
  };

  const handleDeleteSimulation = async (simulationId) => {
    if (!window.confirm('Are you sure you want to delete this simulation?')) return;
    
    try {
      await axios.delete(`${API}/admin/simulations/${simulationId}`);
      toast.success('Simulation deleted successfully');
      fetchSimulations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete simulation');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
              The Case Verse - Admin
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/cases" className="text-slate-700 hover:text-slate-900" data-testid="nav-cases-link">Case Library</Link>
              <Link to="/dashboard" className="text-slate-700 hover:text-slate-900" data-testid="nav-dashboard-link">My Dashboard</Link>
              <Link to="/book-training" className="text-slate-700 hover:text-slate-900" data-testid="nav-book-training-link">Book Training</Link>
              <Link to="/request-case" className="text-slate-700 hover:text-slate-900" data-testid="nav-request-case-link">Request Case</Link>
              <div className="relative" ref={notifDropdownRef}>
                <button
                  onClick={toggleAdminNotifications}
                  className="relative p-1.5 text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="admin-notification-bell"
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotifCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="admin-notification-badge">
                      {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                    </span>
                  )}
                </button>
                {showAdminNotifications && (
                  <div className="absolute right-0 top-10 w-96 bg-white rounded-xl border border-slate-200 shadow-xl z-[100] overflow-hidden" data-testid="admin-notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <span className="font-semibold text-slate-900 text-sm">Notifications</span>
                      {unreadNotifCount > 0 && (
                        <button onClick={markAllNotifRead} className="text-xs text-sky-600 hover:text-sky-800 font-medium" data-testid="admin-mark-all-read-btn">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {adminNotifications.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-6">No notifications yet</p>
                      ) : (
                        adminNotifications.map(n => (
                          <div
                            key={n.id}
                            className={`px-4 py-3 border-b border-slate-50 last:border-0 ${!n.is_read ? 'bg-sky-50/50' : ''}`}
                            data-testid={`admin-notif-item-${n.id}`}
                          >
                            <p className="text-sm text-slate-800 font-medium">{n.title}</p>
                            <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="logout-button">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-8" data-testid="page-title">Admin Dashboard</h1>

          <div className="border-b border-slate-200 mb-8">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('stats')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'stats' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-stats"
              >
                Statistics
              </button>
              <button
                onClick={() => setActiveTab('cases')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'cases' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-cases"
              >
                Manage Cases
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'videos' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-videos"
              >
                Manage Videos
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'users' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-users"
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('access-ids')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'access-ids' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-access-ids"
              >
                Access IDs
              </button>
              <button
                onClick={() => setActiveTab('training')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'training' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-training"
              >
                Training Sessions
              </button>
              <button
                onClick={() => setActiveTab('case-requests')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'case-requests' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-case-requests"
              >
                Case Requests
              </button>
              <button
                onClick={() => setActiveTab('interventions')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'interventions' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-interventions"
              >
                Interventions
              </button>
              <button
                onClick={() => setActiveTab('pages')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'pages' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-pages"
              >
                Pages
              </button>
              <button
                onClick={() => setActiveTab('subscribers')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'subscribers' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-subscribers"
              >
                Subscribers
              </button>
              <button
                onClick={() => setActiveTab('finance')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'finance' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-finance"
              >
                Finance
              </button>
              <button
                onClick={() => setActiveTab('simulations')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'simulations' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-simulations"
              >
                Simulations
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'account' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-account"
              >
                Account
              </button>
            </div>
          </div>

          {activeTab === 'stats' && stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-testid="stats-section">
              <div className="bg-white p-6 rounded border border-slate-200">
                <Users className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-serif font-bold mb-1">{stats.total_users}</div>
                <div className="text-slate-600 text-sm">Total Users</div>
              </div>
              <div className="bg-white p-6 rounded border border-slate-200">
                <BookOpen className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-serif font-bold mb-1">{stats.total_cases}</div>
                <div className="text-slate-600 text-sm">Total Cases</div>
              </div>
              <div className="bg-white p-6 rounded border border-slate-200">
                <Play className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-serif font-bold mb-1">{stats.total_videos || 0}</div>
                <div className="text-slate-600 text-sm">Total Videos</div>
              </div>
              <div className="bg-white p-6 rounded border border-slate-200">
                <Gamepad2 className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-serif font-bold mb-1">{stats.total_simulations || 0}</div>
                <div className="text-slate-600 text-sm">Simulations</div>
              </div>
              <div className="bg-white p-6 rounded border border-slate-200">
                <TrendingUp className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-serif font-bold mb-1">{stats.active_subscriptions}</div>
                <div className="text-slate-600 text-sm">Active Subs</div>
              </div>
              <div className="bg-white p-6 rounded border border-slate-200">
                <DollarSign className="w-10 h-10 text-orange-600 mb-3" />
                <div className="text-3xl font-serif font-bold mb-1">${stats.total_revenue.toFixed(2)}</div>
                <div className="text-slate-600 text-sm">Total Revenue</div>
              </div>
            </div>
          )}

          {activeTab === 'cases' && (
            <div data-testid="cases-section">
              <div className="mb-6">
                <button
                  onClick={() => setShowAddCase(true)}
                  className="px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all flex items-center gap-2"
                  data-testid="add-case-button"
                >
                  <Plus className="w-5 h-5" />
                  Add New Case
                </button>
              </div>

              {showAddCase && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6" data-testid="add-case-form">
                  <h3 className="text-xl font-semibold mb-4">Create New Case</h3>
                  <form onSubmit={handleCreateCase} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                        <input
                          type="text"
                          required
                          value={newCase.title}
                          onChange={(e) => setNewCase({...newCase, title: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="case-title-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Domain * (select all that apply)</label>
                        <div className="border border-slate-300 rounded p-3 max-h-48 overflow-y-auto space-y-2" data-testid="case-domain-checkboxes">
                          {['Business Communication and Soft Skills', 'Economics', 'Entrepreneurship', 'Ethics- Sustainability- Governance- Climate', 'Family Business', 'Finance', 'Human Resources Management', 'Marketing', 'Operations and Logistics', 'Strategy', 'Technology'].map(d => (
                            <label key={d} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                              <input
                                type="checkbox"
                                checked={Array.isArray(newCase.domain) && newCase.domain.includes(d)}
                                onChange={(e) => {
                                  const domains = Array.isArray(newCase.domain) ? [...newCase.domain] : [];
                                  if (e.target.checked) {
                                    domains.push(d);
                                  } else {
                                    const idx = domains.indexOf(d);
                                    if (idx > -1) domains.splice(idx, 1);
                                  }
                                  setNewCase({...newCase, domain: domains});
                                }}
                                className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                data-testid={`domain-checkbox-${d.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}
                              />
                              {d}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Sector *</label>
                        <select
                          required
                          value={newCase.sector}
                          onChange={(e) => setNewCase({...newCase, sector: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="case-sector-select"
                        >
                          <option value="">Select Sector</option>
                          <option value="Advertising, Research and Consulting">Advertising, Research and Consulting</option>
                          <option value="Auto">Auto</option>
                          <option value="Aviation">Aviation</option>
                          <option value="Banking and Finance">Banking and Finance</option>
                          <option value="Healthcare and Pharma">Healthcare and Pharma</option>
                          <option value="Hospitality and Tourism">Hospitality and Tourism</option>
                          <option value="Media and Entertainment">Media and Entertainment</option>
                          <option value="Operations and Logistics">Operations and Logistics</option>
                          <option value="Primary and Core">Primary and Core</option>
                          <option value="Realty and Infra">Realty and Infra</option>
                          <option value="Retail">Retail</option>
                          <option value="StartUps">StartUps</option>
                          <option value="Technology">Technology</option>
                          <option value="Telecom">Telecom</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Geography *</label>
                        <select
                          required
                          value={newCase.geography}
                          onChange={(e) => setNewCase({...newCase, geography: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="case-geography-select"
                        >
                          <option value="India">India</option>
                          <option value="China">China</option>
                          <option value="US">United States</option>
                          <option value="Europe">Europe</option>
                          <option value="Asia">Asia</option>
                          <option value="Africa">Africa</option>
                          <option value="Latin America">Latin America</option>
                          <option value="Middle East">Middle East</option>
                          <option value="Global">Global</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Year *</label>
                        <input
                          type="number"
                          required
                          min="1950"
                          max={new Date().getFullYear() + 1}
                          value={newCase.year}
                          onChange={(e) => setNewCase({...newCase, year: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="case-year-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Concept *</label>
                        <input
                          type="text"
                          required
                          value={newCase.concept}
                          onChange={(e) => setNewCase({...newCase, concept: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          placeholder="e.g., Capital Structure, Brand Management"
                          data-testid="case-concept-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Level * (select all that apply)</label>
                        <div className="border border-slate-300 rounded p-3 space-y-2" data-testid="case-level-checkboxes">
                          {['UG', 'MBA', 'Executive'].map(l => (
                            <label key={l} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                              <input
                                type="checkbox"
                                checked={Array.isArray(newCase.level) && newCase.level.includes(l)}
                                onChange={(e) => {
                                  const levels = Array.isArray(newCase.level) ? [...newCase.level] : [];
                                  if (e.target.checked) {
                                    levels.push(l);
                                  } else {
                                    const idx = levels.indexOf(l);
                                    if (idx > -1) levels.splice(idx, 1);
                                  }
                                  setNewCase({...newCase, level: levels});
                                }}
                                className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                data-testid={`level-checkbox-${l.toLowerCase()}`}
                              />
                              {l === 'UG' ? 'Undergraduate' : l}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Length (Pages)</label>
                        <select
                          value={newCase.length_pages}
                          onChange={(e) => setNewCase({...newCase, length_pages: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="case-length-pages-select"
                        >
                          <option value="">Select length</option>
                          <option value="< 2">&lt; 2 pages</option>
                          <option value="2-5">2-5 pages</option>
                          <option value="6-10">6-10 pages</option>
                          <option value="11-15">11-15 pages</option>
                          <option value="> 15">&gt; 15 pages</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Product Type * (select all that apply)</label>
                        <div className="border border-slate-300 rounded p-3 space-y-2" data-testid="case-product-type-checkboxes">
                          {['Case Study', 'Simulation', 'Games', 'Role Plays'].map(pt => (
                            <label key={pt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                              <input
                                type="checkbox"
                                checked={Array.isArray(newCase.product_type) && newCase.product_type.includes(pt)}
                                onChange={(e) => {
                                  const types = Array.isArray(newCase.product_type) ? [...newCase.product_type] : [];
                                  if (e.target.checked) {
                                    types.push(pt);
                                  } else {
                                    const idx = types.indexOf(pt);
                                    if (idx > -1) types.splice(idx, 1);
                                  }
                                  setNewCase({...newCase, product_type: types});
                                }}
                                className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                                data-testid={`product-type-checkbox-${pt.replace(/\s+/g, '-').toLowerCase()}`}
                              />
                              {pt}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Key Words</label>
                        <input
                          type="text"
                          value={newCase.keywords}
                          onChange={(e) => setNewCase({...newCase, keywords: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          placeholder="Enter keywords separated by commas"
                          data-testid="case-keywords-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Author</label>
                      <input
                        type="text"
                        value={newCase.author}
                        onChange={(e) => setNewCase({...newCase, author: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="Case author name (optional)"
                        data-testid="case-author-input"
                      />
                    </div>
                    
                    <div className="border-t border-slate-200 pt-4">
                      <h3 className="text-lg font-semibold mb-4">File Uploads</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Case Study File * (PDF, Word, or Text)
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.docx,.txt"
                            required
                            onChange={(e) => setCaseFile(e.target.files[0])}
                            className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                            data-testid="case-file-input"
                          />
                          {caseFile && <p className="text-xs text-green-600 mt-1">✓ {caseFile.name}</p>}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Teaching Notes File (Optional - PDF, Word, or Text)
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.docx,.txt"
                            onChange={(e) => setTeachingNoteFile(e.target.files[0])}
                            className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                            data-testid="teaching-note-file-input"
                          />
                          {teachingNoteFile && <p className="text-xs text-green-600 mt-1">✓ {teachingNoteFile.name}</p>}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Student Worksheet File (Optional - PDF, Word, or Text)
                          </label>
                          <input
                            type="file"
                            accept=".pdf,.docx,.txt"
                            onChange={(e) => setWorksheetFile(e.target.files[0])}
                            className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                            data-testid="worksheet-file-input"
                          />
                          {worksheetFile && <p className="text-xs text-green-600 mt-1">✓ {worksheetFile.name}</p>}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                      <textarea
                        required
                        value={newCase.description}
                        onChange={(e) => setNewCase({...newCase, description: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        rows="3"
                        placeholder="Brief description of the case study"
                        data-testid="case-description-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newCase.is_sample}
                        onChange={(e) => setNewCase({...newCase, is_sample: e.target.checked})}
                        className="w-4 h-4"
                        data-testid="case-sample-checkbox"
                      />
                      <label className="text-sm text-slate-700">Mark as sample case (accessible during free trial)</label>
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="submit-case-button">
                        Create Case
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddCase(false)}
                        className="px-6 py-2 border border-slate-300 rounded hover:border-slate-900"
                        data-testid="cancel-case-button"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="bg-white rounded border border-slate-200">
                <table className="w-full">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold">Title</th>
                      <th className="text-left p-4 font-semibold">Domain</th>
                      <th className="text-left p-4 font-semibold">Year</th>
                      <th className="text-left p-4 font-semibold">Sample</th>
                      <th className="text-left p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map((caseItem) => (
                      <tr key={caseItem.id} className="border-b border-slate-200">
                        <td className="p-4">{caseItem.title}</td>
                        <td className="p-4">{Array.isArray(caseItem.domain) ? caseItem.domain.join(', ') : caseItem.domain}</td>
                        <td className="p-4">{caseItem.year}</td>
                        <td className="p-4">{caseItem.is_sample ? 'Yes' : 'No'}</td>
                        <td className="p-4 space-x-2">
                          <label className="text-sky-600 hover:text-sky-700 text-sm cursor-pointer" data-testid={`reupload-case-${caseItem.id}`}>
                            Re-upload
                            <input
                              type="file"
                              accept=".pdf,.docx,.doc"
                              className="hidden"
                              onChange={(e) => { handleReuploadCase(caseItem.id, e.target.files[0]); e.target.value = ''; }}
                            />
                          </label>
                          <button
                            onClick={() => handleDeleteCase(caseItem.id)}
                            className="text-red-600 hover:text-red-700 text-sm"
                            data-testid={`delete-case-${caseItem.id}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'videos' && (
            <div data-testid="videos-section">
              <div className="mb-6">
                <button
                  onClick={() => setShowAddVideo(true)}
                  className="px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all flex items-center gap-2"
                  data-testid="add-video-button"
                >
                  <Plus className="w-5 h-5" />
                  Add New Video
                </button>
              </div>

              {showAddVideo && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6" data-testid="add-video-form">
                  <h3 className="text-xl font-semibold mb-4">Add New Video</h3>
                  <form onSubmit={handleCreateVideo} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Video Title *</label>
                      <input
                        type="text"
                        required
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="e.g., Introduction to Case Study Methodology"
                        data-testid="video-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">YouTube URL *</label>
                      <input
                        type="url"
                        required
                        value={newVideo.youtube_url}
                        onChange={(e) => setNewVideo({...newVideo, youtube_url: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="https://www.youtube.com/watch?v=..."
                        data-testid="video-url-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Paste the full YouTube video URL</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                      <textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        rows="3"
                        placeholder="Brief description of the video content"
                        data-testid="video-description-input"
                      />
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="submit-video-button">
                        Add Video
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddVideo(false);
                          setNewVideo({ title: '', youtube_url: '', description: '', related_case_ids: [] });
                        }}
                        className="px-6 py-2 border border-slate-300 rounded hover:border-slate-900"
                        data-testid="cancel-video-button"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="bg-white rounded border border-slate-200">
                <table className="w-full">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold">Title</th>
                      <th className="text-left p-4 font-semibold">YouTube URL</th>
                      <th className="text-left p-4 font-semibold">Description</th>
                      <th className="text-left p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videos.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="p-4 text-center text-slate-500">No videos added yet</td>
                      </tr>
                    ) : (
                      videos.map((video) => (
                        <tr key={video.id} className="border-b border-slate-200">
                          <td className="p-4">{video.title}</td>
                          <td className="p-4">
                            <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 text-sm">
                              View on YouTube
                            </a>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{video.description || '-'}</td>
                          <td className="p-4">
                            <button
                              onClick={() => handleDeleteVideo(video.id)}
                              className="text-red-600 hover:text-red-700 text-sm"
                              data-testid={`delete-video-${video.id}`}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="bg-white rounded border border-slate-200 overflow-x-auto" data-testid="users-section">
              <table className="w-full min-w-[1000px]">
                <thead className="border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-semibold">Name</th>
                    <th className="text-left p-4 font-semibold">Email</th>
                    <th className="text-left p-4 font-semibold">Role</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-left p-4 font-semibold">Trial Start</th>
                    <th className="text-left p-4 font-semibold">Trial End</th>
                    <th className="text-left p-4 font-semibold">Subscription End</th>
                    <th className="text-left p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const trialStart = user.trial_start ? new Date(user.trial_start) : null;
                    const trialEnd = user.trial_end ? new Date(user.trial_end) : null;
                    const subscriptionEnd = user.subscription_end ? new Date(user.subscription_end) : null;
                    const now = new Date();
                    const isTrialExpired = trialEnd && now > trialEnd;
                    const isSubscriptionExpired = subscriptionEnd && now > subscriptionEnd;
                    
                    return (
                    <tr key={user.id} className="border-b border-slate-200">
                      <td className="p-4">{user.name}</td>
                      <td className="p-4 text-sm">{user.email}</td>
                      <td className="p-4 capitalize">{user.role}</td>
                      <td className="p-4">
                        {user.institution_id ? (
                          <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                            Institution
                          </span>
                        ) : user.is_trial ? (
                          <span className={`px-2 py-1 rounded text-xs ${
                            isTrialExpired ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {isTrialExpired ? 'Trial Expired' : 'Trial Active'}
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded text-xs ${
                            isSubscriptionExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {isSubscriptionExpired ? 'Subscription Expired' : 'Subscribed'}
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {trialStart ? trialStart.toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {trialEnd ? (
                          <span className={isTrialExpired ? 'text-red-600' : 'text-slate-600'}>
                            {trialEnd.toLocaleDateString()}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {subscriptionEnd ? (
                          <span className={isSubscriptionExpired ? 'text-red-600' : 'text-green-600'}>
                            {subscriptionEnd.toLocaleDateString()}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <select
                            value={user.role}
                            onChange={async (e) => {
                              try {
                                await axios.put(`${API}/admin/users/${user.id}/role`, { role: e.target.value });
                                toast.success(`${user.name}'s role updated to ${e.target.value}`);
                                fetchUsers();
                              } catch (error) {
                                toast.error('Failed to update role');
                              }
                            }}
                            className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                            data-testid={`role-select-${user.id}`}
                          >
                            <option value="user">User</option>
                            <option value="instructor">Instructor</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete user ${user.email}? This permanently removes their account and cannot be undone.`)) return;
                              try {
                                await axios.delete(`${API}/admin/users/${user.id}`);
                                toast.success(`${user.email} deleted`);
                                fetchUsers();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to delete user');
                              }
                            }}
                            className="px-3 py-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'access-ids' && (
            <div data-testid="access-ids-section" className="space-y-6">
              {/* Generate form */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-1">Generate access IDs</h3>
                <p className="text-sm text-slate-500 mb-4">Provision access IDs for an institute. IDs are valid for <strong>365 days</strong> and can be redeemed by one student/faculty each. If an institute email is provided, the codes are emailed automatically.</p>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Institute name</label>
                    <input
                      type="text"
                      value={newAccessIdForm.institute_name}
                      onChange={(e) => setNewAccessIdForm({ ...newAccessIdForm, institute_name: e.target.value })}
                      placeholder="e.g. IIM Bangalore"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="access-id-institute-name-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Institute email (optional)</label>
                    <input
                      type="email"
                      value={newAccessIdForm.institute_email}
                      onChange={(e) => setNewAccessIdForm({ ...newAccessIdForm, institute_email: e.target.value })}
                      placeholder="learn@institute.edu"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="access-id-institute-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Generate for</label>
                    <select
                      value={newAccessIdForm.category}
                      onChange={(e) => setNewAccessIdForm({ ...newAccessIdForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="access-id-category-select"
                    >
                      <option value="student">Student</option>
                      <option value="faculty">Faculty</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={newAccessIdForm.quantity}
                      onChange={(e) => setNewAccessIdForm({ ...newAccessIdForm, quantity: Math.max(1, Math.min(500, parseInt(e.target.value || '1', 10))) })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="access-id-quantity-input"
                    />
                  </div>
                  <div className="md:col-span-5 flex justify-end">
                    <button
                      onClick={handleGenerateAccessIds}
                      disabled={generatingIds || !newAccessIdForm.institute_name.trim()}
                      className="px-5 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 disabled:opacity-50"
                      data-testid="generate-access-ids-btn"
                    >
                      {generatingIds ? 'Generating…' : `Generate ${newAccessIdForm.quantity} ID${newAccessIdForm.quantity > 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              </div>

              {/* Repository */}
              {(() => {
                const institutes = Array.from(new Set(accessIds.map((a) => a.institute_name).filter(Boolean))).sort();
                const filtered = accessIds.filter((a) => {
                  if (accessIdFilter.institute !== 'all' && a.institute_name !== accessIdFilter.institute) return false;
                  if (accessIdFilter.category !== 'all' && a.category !== accessIdFilter.category) return false;
                  if (accessIdFilter.status !== 'all') {
                    const expiresPast = a.expires_at && new Date() > new Date(a.expires_at);
                    const effective = a.status === 'redeemed' ? 'redeemed' : (a.status === 'expired' || expiresPast ? 'expired' : 'active');
                    if (effective !== accessIdFilter.status) return false;
                  }
                  return true;
                });

                const handleCopyCodes = async () => {
                  if (filtered.length === 0) { toast.error('No codes match the current filter'); return; }
                  try {
                    await navigator.clipboard.writeText(filtered.map((a) => a.code).join('\n'));
                    toast.success(`Copied ${filtered.length} code${filtered.length > 1 ? 's' : ''} to clipboard`);
                  } catch {
                    toast.error('Clipboard not available — please copy manually');
                  }
                };

                const handleExportCsv = () => {
                  if (filtered.length === 0) { toast.error('No rows match the current filter'); return; }
                  const escape = (v) => {
                    const s = v == null ? '' : String(v);
                    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                  };
                  const rows = [
                    ['Code','Institute','Institute Email','Category','Generated On','Expires On','Status','Redeemed By','Redeemed At'],
                    ...filtered.map((a) => {
                      const expiresPast = a.expires_at && new Date() > new Date(a.expires_at);
                      const effective = a.status === 'redeemed' ? 'redeemed' : (a.status === 'expired' || expiresPast ? 'expired' : 'active');
                      return [
                        a.code,
                        a.institute_name || '',
                        a.institute_email || '',
                        a.category || '',
                        a.created_at ? new Date(a.created_at).toISOString().slice(0, 10) : '',
                        a.expires_at ? new Date(a.expires_at).toISOString().slice(0, 10) : '',
                        effective,
                        a.redeemed_by_email || '',
                        a.redeemed_at ? new Date(a.redeemed_at).toISOString().slice(0, 10) : '',
                      ];
                    }),
                  ];
                  const csv = rows.map((r) => r.map(escape).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const filenameParts = ['access-ids'];
                  if (accessIdFilter.institute !== 'all') filenameParts.push(accessIdFilter.institute.replace(/[^a-z0-9]+/gi, '-').toLowerCase());
                  if (accessIdFilter.category !== 'all') filenameParts.push(accessIdFilter.category);
                  if (accessIdFilter.status !== 'all') filenameParts.push(accessIdFilter.status);
                  filenameParts.push(new Date().toISOString().slice(0, 10));
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `${filenameParts.join('_')}.csv`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(link.href);
                  toast.success(`Exported ${filtered.length} row${filtered.length > 1 ? 's' : ''} to CSV`);
                };

                return (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="access-ids-repository">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
                      <h3 className="text-lg font-serif font-semibold text-slate-900">Access ID Repository ({filtered.length}{filtered.length !== accessIds.length ? ` of ${accessIds.length}` : ''})</h3>
                      <p className="text-xs text-slate-500">Expired IDs are auto-marked. Delete anytime to clean up.</p>
                    </div>
                    {/* Filters + actions */}
                    <div className="flex flex-wrap items-end gap-3 px-6 py-3 bg-slate-50 border-b border-slate-100">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Institute</label>
                        <select
                          value={accessIdFilter.institute}
                          onChange={(e) => setAccessIdFilter({ ...accessIdFilter, institute: e.target.value })}
                          className="px-3 py-1.5 border border-slate-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 min-w-[200px]"
                          data-testid="access-id-filter-institute"
                        >
                          <option value="all">All institutes ({institutes.length})</option>
                          {institutes.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
                        <select
                          value={accessIdFilter.category}
                          onChange={(e) => setAccessIdFilter({ ...accessIdFilter, category: e.target.value })}
                          className="px-3 py-1.5 border border-slate-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="access-id-filter-category"
                        >
                          <option value="all">All</option>
                          <option value="student">Student</option>
                          <option value="faculty">Faculty</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                        <select
                          value={accessIdFilter.status}
                          onChange={(e) => setAccessIdFilter({ ...accessIdFilter, status: e.target.value })}
                          className="px-3 py-1.5 border border-slate-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="access-id-filter-status"
                        >
                          <option value="all">All</option>
                          <option value="active">Active</option>
                          <option value="redeemed">Redeemed</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>
                      <div className="flex gap-2 ml-auto">
                        <button
                          onClick={handleCopyCodes}
                          disabled={filtered.length === 0}
                          className="px-3 py-1.5 bg-slate-900 text-white rounded text-sm hover:bg-slate-800 disabled:opacity-50"
                          data-testid="copy-codes-btn"
                        >
                          Copy {filtered.length} code{filtered.length !== 1 ? 's' : ''}
                        </button>
                        <button
                          onClick={handleExportCsv}
                          disabled={filtered.length === 0}
                          className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded text-sm hover:bg-slate-100 disabled:opacity-50"
                          data-testid="export-csv-btn"
                        >
                          Export CSV
                        </button>
                        {(accessIdFilter.institute !== 'all' || accessIdFilter.category !== 'all' || accessIdFilter.status !== 'all') && (
                          <button
                            onClick={() => setAccessIdFilter({ institute: 'all', category: 'all', status: 'all' })}
                            className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded text-sm hover:bg-slate-100"
                            data-testid="clear-filters-btn"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    </div>
                    {accessIds.length === 0 ? (
                      <p className="text-center text-slate-500 text-sm py-10" data-testid="no-access-ids-message">No access IDs generated yet.</p>
                    ) : filtered.length === 0 ? (
                      <p className="text-center text-slate-500 text-sm py-10" data-testid="no-access-ids-filtered-message">No access IDs match the current filter.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="text-left px-4 py-3 font-medium">Code</th>
                              <th className="text-left px-4 py-3 font-medium">Institute</th>
                              <th className="text-left px-4 py-3 font-medium">Category</th>
                              <th className="text-left px-4 py-3 font-medium">Generated on</th>
                              <th className="text-left px-4 py-3 font-medium">Expires</th>
                              <th className="text-left px-4 py-3 font-medium">Status</th>
                              <th className="text-left px-4 py-3 font-medium">Redeemed by</th>
                              <th className="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {filtered.map((a) => {
                              const created = a.created_at ? new Date(a.created_at) : null;
                              const expires = a.expires_at ? new Date(a.expires_at) : null;
                              const isExpired = a.status === 'expired' || (expires && new Date() > expires);
                              const statusColor =
                                a.status === 'redeemed' ? 'bg-green-100 text-green-700' :
                                isExpired ? 'bg-slate-200 text-slate-600' :
                                'bg-orange-100 text-orange-700';
                              return (
                                <tr key={a.id} data-testid={`access-id-row-${a.id}`}>
                                  <td className="px-4 py-2 font-mono text-xs">{a.code}</td>
                                  <td className="px-4 py-2">
                                    <div className="font-medium text-slate-900">{a.institute_name}</div>
                                    {a.institute_email && <div className="text-xs text-slate-500">{a.institute_email}</div>}
                                  </td>
                                  <td className="px-4 py-2 capitalize">{a.category}</td>
                                  <td className="px-4 py-2 text-slate-600">{created ? created.toLocaleDateString() : '-'}</td>
                                  <td className="px-4 py-2 text-slate-600">{expires ? expires.toLocaleDateString() : '-'}</td>
                                  <td className="px-4 py-2">
                                    <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColor}`}>{isExpired && a.status !== 'redeemed' ? 'expired' : a.status}</span>
                                  </td>
                                  <td className="px-4 py-2 text-xs text-slate-600">{a.redeemed_by_email || '—'}</td>
                                  <td className="px-4 py-2 text-right">
                                    <button
                                      onClick={() => handleDeleteAccessId(a.id, a.code)}
                                      className="px-3 py-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50"
                                      data-testid={`delete-access-id-${a.id}`}
                                    >Delete</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Training Sessions Tab */}
          {activeTab === 'training' && (
            <div data-testid="training-sessions-section">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Training Session Bookings</h2>
              {trainingSessions.length === 0 ? (
                <p className="text-slate-500">No training session bookings yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="training-sessions-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Requester</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Role</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Intervention</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Preferred Date</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Notes</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {trainingSessions.map(session => (
                        <tr key={session.id} data-testid={`training-row-${session.id}`}>
                          <td className="p-4">
                            <div className="text-sm font-medium text-slate-900">{session.user_name}</div>
                            <div className="text-xs text-slate-500">{session.user_email}</div>
                          </td>
                          <td className="p-4 text-sm text-slate-600 capitalize">{session.user_role?.replace('_', ' ')}</td>
                          <td className="p-4 text-sm text-slate-900">{session.intervention_name}</td>
                          <td className="p-4 text-sm text-slate-600">{session.preferred_date}</td>
                          <td className="p-4 text-sm text-slate-500 max-w-xs truncate">{session.notes || '-'}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                              session.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                              session.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              session.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>{session.status}</span>
                          </td>
                          <td className="p-4">
                            <select
                              value={session.status}
                              onChange={(e) => handleUpdateTrainingStatus(session.id, e.target.value)}
                              className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                              data-testid={`training-status-${session.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Case Requests Tab */}
          {activeTab === 'case-requests' && (
            <div data-testid="case-requests-section">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Case Requests</h2>
              {caseRequests.length === 0 ? (
                <p className="text-slate-500">No case requests yet.</p>
              ) : (
                <div className="space-y-4">
                  {caseRequests.map(req => (
                    <div key={req.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`case-req-${req.id}`}>
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <span className="text-sm font-medium text-slate-900">{req.user_name}</span>
                          <span className="text-sm text-slate-500 ml-2">({req.user_email})</span>
                          <span className="text-xs text-slate-400 ml-2 capitalize">{req.user_role?.replace('_', ' ')}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          req.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          req.status === 'in_review' ? 'bg-blue-100 text-blue-800' :
                          req.status === 'fulfilled' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>{req.status === 'in_review' ? 'In Review' : req.status}</span>
                      </div>
                      <p className="text-slate-700 mb-3">{req.description}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400">{new Date(req.created_at).toLocaleDateString()}</span>
                        <select
                          value={req.status}
                          onChange={(e) => handleUpdateCaseRequestStatus(req.id, e.target.value)}
                          className="px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid={`case-req-status-${req.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_review">In Review</option>
                          <option value="fulfilled">Fulfilled</option>
                          <option value="declined">Declined</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Interventions Tab */}
          {activeTab === 'interventions' && (
            <div data-testid="interventions-section">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Manage Interventions</h2>
              <form onSubmit={handleCreateIntervention} className="bg-white rounded-xl border border-slate-200 p-6 mb-6 flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newIntervention.name}
                    onChange={(e) => setNewIntervention({ ...newIntervention, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="e.g. Case Teaching Workshop"
                    data-testid="intervention-name-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={newIntervention.description}
                    onChange={(e) => setNewIntervention({ ...newIntervention, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="Brief description..."
                    data-testid="intervention-desc-input"
                  />
                </div>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
                  data-testid="add-intervention-btn"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </form>

              {interventions.length === 0 ? (
                <p className="text-slate-500">No interventions created yet.</p>
              ) : (
                <div className="space-y-3">
                  {interventions.map(i => (
                    <div key={i.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`intervention-${i.id}`}>
                      <div>
                        <h3 className="font-medium text-slate-900">{i.name}</h3>
                        {i.description && <p className="text-sm text-slate-500">{i.description}</p>}
                      </div>
                      <button
                        onClick={() => handleDeleteIntervention(i.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                        data-testid={`delete-intervention-${i.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Pages CMS Tab */}
          {activeTab === 'pages' && (
            <div data-testid="pages-section">
              {/* Subscription Pricing Editor */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="pricing-editor">
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Subscription Pricing</h2>
                <p className="text-sm text-slate-500 mb-5">Set subscription prices for Indian and International subscribers. Changes reflect immediately on the subscription page.</p>
                <form onSubmit={handleSavePricing} className="space-y-6">
                  {/* Academic - Per ID pricing */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">Academic Institution - Per ID</h3>
                    <p className="text-xs text-slate-500 mb-3">Academic institutes pay per access ID (student or faculty). IDs are valid for 365 days.</p>
                    <div className="flex items-end gap-4 flex-wrap">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">India — price per ID (₹)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={pricing.academic_inr_per_id ?? 500}
                          onChange={(e) => setPricing({ ...pricing, academic_inr_per_id: parseFloat(e.target.value) || 0 })}
                          className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="pricing-academic-inr-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">International — price per ID ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={pricing.academic_usd_per_id ?? 10}
                          onChange={(e) => setPricing({ ...pricing, academic_usd_per_id: parseFloat(e.target.value) || 0 })}
                          className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="pricing-academic-usd-input"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Corporate - India Pricing */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">Corporate - India (INR)</h3>
                    <div className="flex items-end gap-4 flex-wrap">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">6 Months (₹)</label>
                        <input type="number" min="0" step="0.01" value={pricing.corp_inr_six_months}
                          onChange={(e) => setPricing({ ...pricing, corp_inr_six_months: parseFloat(e.target.value) || 0 })}
                          className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="pricing-corp-inr-6m-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">12 Months (₹)</label>
                        <input type="number" min="0" step="0.01" value={pricing.corp_inr_twelve_months}
                          onChange={(e) => setPricing({ ...pricing, corp_inr_twelve_months: parseFloat(e.target.value) || 0 })}
                          className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="pricing-corp-inr-12m-input" />
                      </div>
                    </div>
                  </div>
                  {/* Corporate - International Pricing */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 uppercase tracking-wide">Corporate - International (USD)</h3>
                    <div className="flex items-end gap-4 flex-wrap">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">6 Months ($)</label>
                        <input type="number" min="0" step="0.01" value={pricing.corp_usd_six_months}
                          onChange={(e) => setPricing({ ...pricing, corp_usd_six_months: parseFloat(e.target.value) || 0 })}
                          className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="pricing-corp-usd-6m-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">12 Months ($)</label>
                        <input type="number" min="0" step="0.01" value={pricing.corp_usd_twelve_months}
                          onChange={(e) => setPricing({ ...pricing, corp_usd_twelve_months: parseFloat(e.target.value) || 0 })}
                          className="w-40 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="pricing-corp-usd-12m-input" />
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={savingPricing}
                    className="px-5 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                    data-testid="save-pricing-btn"
                  >
                    {savingPricing ? 'Saving...' : 'Save All Pricing'}
                  </button>
                </form>
              </div>

              <h2 className="text-xl font-semibold text-slate-900 mb-4">Manage Page Content</h2>
              <p className="text-sm text-slate-500 mb-6">Edit the HTML content of static pages. Use standard HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt; for formatting.</p>

              {pages.length === 0 ? (
                <p className="text-slate-500">No pages found. Pages will be created on first server startup.</p>
              ) : (
                <div className="space-y-4">
                  {pages.map(page => (
                    <div key={page.slug} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`page-${page.slug}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900">{page.title}</h3>
                          <span className="text-xs text-slate-400">/{page.slug}</span>
                          {page.updated_at && (
                            <span className="text-xs text-slate-400 ml-3">Last updated: {new Date(page.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                        {editingPage !== page.slug ? (
                          <button
                            onClick={() => handleEditPage(page)}
                            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm"
                            data-testid={`edit-page-${page.slug}`}
                          >
                            Edit
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSavePage(page.slug)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                              data-testid={`save-page-${page.slug}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => { setEditingPage(null); setPageContent(''); }}
                              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm"
                              data-testid={`cancel-page-${page.slug}`}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>

                      {editingPage === page.slug ? (
                        <textarea
                          value={pageContent}
                          onChange={(e) => setPageContent(e.target.value)}
                          rows={16}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                          placeholder="Enter HTML content..."
                          data-testid={`page-editor-${page.slug}`}
                        />
                      ) : (
                        <div className="prose prose-sm prose-slate max-w-none border border-slate-100 rounded-lg p-4 bg-slate-50 max-h-48 overflow-y-auto" dangerouslySetInnerHTML={{ __html: page.content || '<em>No content</em>' }} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Research Board Management */}
              <div className="mt-10" data-testid="research-section">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Manage Research Board</h2>

                <form onSubmit={handleCreateArticle} className="bg-white rounded-xl border border-slate-200 p-6 mb-8 space-y-4" data-testid="new-article-form">
                  <h3 className="font-medium text-slate-900">Add New Product</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Product Title *</label>
                    <input type="text" value={newArticle.title} onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" placeholder="e.g. Global Retail Brands - The Chinese Experience" data-testid="article-title-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
                    <input type="text" value={newArticle.author} onChange={(e) => setNewArticle({ ...newArticle, author: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" placeholder="Default: The Case Verse" data-testid="article-author-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Product Type</label>
                    <input type="text" value={newArticle.product_type} onChange={(e) => setNewArticle({ ...newArticle, product_type: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" placeholder="e.g. Book of cases, Case Pack" data-testid="article-type-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Price (INR)</label>
                    <input type="number" min="0" step="0.01" value={newArticle.price} onChange={(e) => setNewArticle({ ...newArticle, price: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" placeholder="e.g. 12000" data-testid="article-price-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description / Content * (HTML supported)</label>
                  <textarea value={newArticle.content} onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })} rows={6} className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" placeholder="Brief description shown in 'Know More' popup. HTML supported." data-testid="article-content-input" />
                </div>
                <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800" data-testid="publish-article-btn">
                  <Plus className="w-4 h-4" /> Add Product
                </button>
              </form>

              {researchArticles.length === 0 ? (
                <p className="text-slate-500">No products added yet.</p>
              ) : (
                <div className="space-y-4">
                  {researchArticles.map((article, index) => (
                    <div key={article.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`admin-article-${article.id}`}>
                      {editingArticle?.id === article.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Title</label>
                              <input type="text" value={editingArticle.title} onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`edit-title-${article.id}`} />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Author</label>
                              <input type="text" value={editingArticle.author || ''} onChange={(e) => setEditingArticle({ ...editingArticle, author: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Product Type</label>
                              <input type="text" value={editingArticle.product_type || ''} onChange={(e) => setEditingArticle({ ...editingArticle, product_type: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`edit-type-${article.id}`} />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-500 mb-1">Price (INR)</label>
                              <input type="number" min="0" step="0.01" value={editingArticle.price || 0} onChange={(e) => setEditingArticle({ ...editingArticle, price: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`edit-price-${article.id}`} />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Description (HTML)</label>
                            <textarea value={editingArticle.content || ''} onChange={(e) => setEditingArticle({ ...editingArticle, content: e.target.value })} rows={5} className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid={`edit-content-${article.id}`} />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={handleSaveArticle} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-testid={`save-article-${article.id}`}>Save</button>
                            <button onClick={() => setEditingArticle(null)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-xs text-slate-400">{index + 1}.</span>
                              <h3 className="font-semibold text-slate-900">{article.title}</h3>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              {article.product_type && <span>Type: {article.product_type}</span>}
                              {article.price > 0 && <span className="font-medium text-slate-700">Rs {article.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>}
                              <span>{new Date(article.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleEditArticle(article)} className="text-blue-600 hover:text-blue-800 text-sm" data-testid={`edit-article-${article.id}`}>Edit</button>
                            <button onClick={() => handleDeleteArticle(article.id)} className="text-red-500 hover:text-red-700 text-sm" data-testid={`delete-article-${article.id}`}>Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
          {/* Finance Tab */}
          {activeTab === 'subscribers' && (
            <div data-testid="subscribers-admin-section">
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Add Subscriber</h3>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <input
                    type="text"
                    placeholder="Subscriber name (e.g. IIM Bangalore)"
                    value={newSubscriber.name}
                    onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })}
                    className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="subscriber-name-input"
                  />
                  <select
                    value={newSubscriber.category}
                    onChange={(e) => setNewSubscriber({ ...newSubscriber, category: e.target.value })}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="subscriber-category-select"
                  >
                    <option value="Academic">Academic</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                  <input
                    type="url"
                    placeholder="Logo URL (optional)"
                    value={newSubscriber.logo_url}
                    onChange={(e) => setNewSubscriber({ ...newSubscriber, logo_url: e.target.value })}
                    className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="subscriber-logo-input"
                  />
                  <input
                    type="number"
                    placeholder="Order"
                    value={newSubscriber.display_order}
                    onChange={(e) => setNewSubscriber({ ...newSubscriber, display_order: e.target.value })}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="subscriber-order-input"
                  />
                </div>
                <div className="flex items-center justify-between mt-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={newSubscriber.active}
                      onChange={(e) => setNewSubscriber({ ...newSubscriber, active: e.target.checked })}
                      data-testid="subscriber-active-checkbox"
                    />
                    Visible on Our Subscribers page
                  </label>
                  <button
                    onClick={handleAddSubscriber}
                    disabled={savingSubscriber || !newSubscriber.name.trim()}
                    className="px-5 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50"
                    data-testid="add-subscriber-btn"
                  >
                    {savingSubscriber ? 'Saving...' : 'Add Subscriber'}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                  <h3 className="text-lg font-serif font-semibold text-slate-900">All Subscribers ({subscribers.length})</h3>
                </div>
                {subscribers.length === 0 ? (
                  <p className="text-center text-slate-500 text-sm py-10" data-testid="no-subscribers-message">No subscribers yet. Add your first subscriber above.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-6 py-3 font-medium">Order</th>
                          <th className="text-left px-6 py-3 font-medium">Name</th>
                          <th className="text-left px-6 py-3 font-medium">Category</th>
                          <th className="text-left px-6 py-3 font-medium">Logo</th>
                          <th className="text-left px-6 py-3 font-medium">Visible</th>
                          <th className="text-right px-6 py-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {subscribers.map((s) => {
                          const isEditing = editingSubscriber?.id === s.id;
                          return (
                            <tr key={s.id} data-testid={`subscriber-row-${s.id}`}>
                              <td className="px-6 py-3">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editingSubscriber.display_order}
                                    onChange={(e) => setEditingSubscriber({ ...editingSubscriber, display_order: e.target.value })}
                                    className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                                  />
                                ) : (
                                  <span className="text-slate-500">{s.display_order}</span>
                                )}
                              </td>
                              <td className="px-6 py-3 font-medium text-slate-900">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editingSubscriber.name}
                                    onChange={(e) => setEditingSubscriber({ ...editingSubscriber, name: e.target.value })}
                                    className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                                  />
                                ) : (
                                  s.name
                                )}
                              </td>
                              <td className="px-6 py-3">
                                {isEditing ? (
                                  <select
                                    value={editingSubscriber.category}
                                    onChange={(e) => setEditingSubscriber({ ...editingSubscriber, category: e.target.value })}
                                    className="px-2 py-1 border border-slate-300 rounded text-sm"
                                  >
                                    <option value="Academic">Academic</option>
                                    <option value="Corporate">Corporate</option>
                                  </select>
                                ) : (
                                  <span className={`px-2 py-0.5 rounded text-xs ${s.category === 'Corporate' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{s.category}</span>
                                )}
                              </td>
                              <td className="px-6 py-3">
                                {isEditing ? (
                                  <input
                                    type="url"
                                    value={editingSubscriber.logo_url || ''}
                                    onChange={(e) => setEditingSubscriber({ ...editingSubscriber, logo_url: e.target.value })}
                                    className="w-48 px-2 py-1 border border-slate-300 rounded text-sm"
                                    placeholder="Logo URL"
                                  />
                                ) : s.logo_url ? (
                                  <img src={s.logo_url} alt={s.name} className="w-8 h-8 object-contain rounded" />
                                ) : (
                                  <span className="text-slate-400 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-6 py-3">
                                <button
                                  onClick={() => toggleSubscriberActive(s)}
                                  className={`px-2 py-0.5 rounded text-xs ${s.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}
                                  data-testid={`toggle-active-${s.id}`}
                                >
                                  {s.active ? 'Visible' : 'Hidden'}
                                </button>
                              </td>
                              <td className="px-6 py-3 text-right space-x-2">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={handleUpdateSubscriber}
                                      disabled={savingSubscriber}
                                      className="px-3 py-1 bg-slate-900 text-white rounded text-xs hover:bg-slate-800 disabled:opacity-50"
                                      data-testid={`save-subscriber-${s.id}`}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingSubscriber(null)}
                                      className="px-3 py-1 border border-slate-300 text-slate-700 rounded text-xs hover:bg-slate-50"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setEditingSubscriber({ ...s })}
                                      className="px-3 py-1 border border-slate-300 text-slate-700 rounded text-xs hover:bg-slate-50"
                                      data-testid={`edit-subscriber-${s.id}`}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSubscriber(s.id)}
                                      className="px-3 py-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50"
                                      data-testid={`delete-subscriber-${s.id}`}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Finance Tab */}
          {activeTab === 'finance' && (
            <div data-testid="finance-section">
              {/* Revenue Overview Cards */}
              {financeOverview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-total">
                    <div className="text-sm text-slate-500 mb-1">Total Revenue</div>
                    <div className="text-2xl font-bold text-slate-900">₹{financeOverview.total_revenue.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-today">
                    <div className="text-sm text-slate-500 mb-1">Today</div>
                    <div className="text-2xl font-bold text-slate-900">₹{financeOverview.today_revenue.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-month">
                    <div className="text-sm text-slate-500 mb-1">This Month</div>
                    <div className="text-2xl font-bold text-slate-900">₹{financeOverview.month_revenue.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-year">
                    <div className="text-sm text-slate-500 mb-1">This Year</div>
                    <div className="text-2xl font-bold text-slate-900">₹{financeOverview.year_revenue.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              )}

              {/* Transaction Stats */}
              {financeOverview && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                  <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{financeOverview.total_transactions}</div>
                    <div className="text-xs text-slate-500">Total</div>
                  </div>
                  <div className="bg-white rounded-xl border border-green-200 p-4 text-center">
                    <div className="text-2xl font-bold text-green-700">{financeOverview.successful_transactions}</div>
                    <div className="text-xs text-green-600">Successful</div>
                  </div>
                  <div className="bg-white rounded-xl border border-red-200 p-4 text-center">
                    <div className="text-2xl font-bold text-red-700">{financeOverview.failed_transactions}</div>
                    <div className="text-xs text-red-600">Failed</div>
                  </div>
                  <div className="bg-white rounded-xl border border-amber-200 p-4 text-center">
                    <div className="text-2xl font-bold text-amber-700">{financeOverview.pending_transactions}</div>
                    <div className="text-xs text-amber-600">Pending</div>
                  </div>
                  <div className="bg-white rounded-xl border border-blue-200 p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{financeOverview.active_subscriptions}</div>
                    <div className="text-xs text-blue-600">Active Subs</div>
                  </div>
                </div>
              )}

              {/* GST Summary */}
              {financeOverview && financeOverview.total_gst > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 mb-8">
                  <h3 className="font-semibold text-slate-900 mb-2">GST Summary (18%)</h3>
                  <div className="flex gap-8 text-sm">
                    <span className="text-slate-600">Total GST Collected: <strong className="text-slate-900">₹{financeOverview.total_gst.toLocaleString('en-IN')}</strong></span>
                    <span className="text-slate-600">CGST (9%): <strong className="text-slate-900">₹{(financeOverview.total_gst / 2).toLocaleString('en-IN')}</strong></span>
                    <span className="text-slate-600">SGST (9%): <strong className="text-slate-900">₹{(financeOverview.total_gst / 2).toLocaleString('en-IN')}</strong></span>
                  </div>
                </div>
              )}

              {/* Filters & Export */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Start Date</label>
                    <input type="date" value={txnStartDate} onChange={(e) => setTxnStartDate(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid="txn-start-date" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">End Date</label>
                    <input type="date" value={txnEndDate} onChange={(e) => setTxnEndDate(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid="txn-end-date" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Status</label>
                    <select value={txnStatusFilter} onChange={(e) => setTxnStatusFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" data-testid="txn-status-filter">
                      <option value="all">All</option>
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>
                  <button onClick={handleFilterTransactions} className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm" data-testid="filter-btn">Filter</button>
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => handleExportCSV('transactions')} className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-testid="export-csv-btn">
                      <Download className="w-3 h-3" /> Transactions CSV
                    </button>
                    <button onClick={() => handleExportCSV('gst')} className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-testid="export-gst-btn">
                      <Download className="w-3 h-3" /> GST Report
                    </button>
                  </div>
                </div>
              </div>

              {/* Transactions Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
                <h3 className="px-5 pt-5 pb-3 font-semibold text-slate-900">Transactions</h3>
                {transactions.length === 0 ? (
                  <p className="px-5 pb-5 text-slate-500 text-sm">No transactions found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="transactions-table">
                      <thead className="bg-slate-50 border-y border-slate-200">
                        <tr>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">S.No</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">User</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Package</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Transaction ID</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Amount</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Status</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Date</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Invoice</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.map((t, idx) => (
                          <tr key={t.id} data-testid={`txn-row-${t.id}`}>
                            <td className="p-3 text-sm text-slate-500">{idx + 1}</td>
                            <td className="p-3">
                              <div className="text-sm font-medium text-slate-900">{t.user_name || '-'}</div>
                              <div className="text-xs text-slate-500">{t.user_email || t.user_id}</div>
                            </td>
                            <td className="p-3 text-sm text-slate-700">{t.package_label || t.metadata?.package_id || '-'}</td>
                            <td className="p-3 text-xs font-mono text-slate-500">{t.id?.slice(0, 8)}...</td>
                            <td className="p-3 text-sm font-medium text-slate-900">₹{t.amount?.toLocaleString('en-IN')}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                t.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                                t.payment_status === 'failed' ? 'bg-red-100 text-red-800' :
                                t.payment_status === 'refunded' ? 'bg-purple-100 text-purple-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>{t.payment_status}</span>
                            </td>
                            <td className="p-3 text-xs text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                            <td className="p-3">
                              {t.payment_status === 'paid' && (
                                <button onClick={() => handleViewInvoice(t.id)} className="text-blue-600 hover:text-blue-800 text-xs underline" data-testid={`invoice-btn-${t.id}`}>View</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Active Subscriptions */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <h3 className="px-5 pt-5 pb-3 font-semibold text-slate-900">Active Subscriptions ({activeSubscriptions.length})</h3>
                {activeSubscriptions.length === 0 ? (
                  <p className="px-5 pb-5 text-slate-500 text-sm">No active subscriptions.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="subscriptions-table">
                      <thead className="bg-slate-50 border-y border-slate-200">
                        <tr>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">User</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Email</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Role</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Subscription End</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {activeSubscriptions.map(sub => (
                          <tr key={sub.id}>
                            <td className="p-3 text-sm font-medium text-slate-900">{sub.name}</td>
                            <td className="p-3 text-sm text-slate-600">{sub.email}</td>
                            <td className="p-3 text-sm text-slate-600 capitalize">{sub.role?.replace('_', ' ')}</td>
                            <td className="p-3 text-sm text-slate-600">{sub.subscription_end ? new Date(sub.subscription_end).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Invoice Modal */}
              {invoiceModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" data-testid="invoice-modal">
                  <div className="bg-white rounded-xl max-w-lg w-full p-8 relative">
                    <button onClick={() => setInvoiceModal(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-serif font-bold text-slate-900">The Case Verse</h2>
                      <p className="text-sm text-slate-500">Tax Invoice</p>
                    </div>
                    <div className="border-t border-b border-slate-200 py-4 mb-4 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Invoice No:</span><span className="font-medium">{invoiceModal.invoice_number}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Date:</span><span>{new Date(invoiceModal.date).toLocaleDateString()}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Customer:</span><span>{invoiceModal.user_name}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Email:</span><span>{invoiceModal.user_email}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Package:</span><span>{invoiceModal.package}</span></div>
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between"><span className="text-slate-500">Base Amount:</span><span>₹{invoiceModal.base_amount?.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">CGST (9%):</span><span>₹{(invoiceModal.gst_amount / 2)?.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">SGST (9%):</span><span>₹{(invoiceModal.gst_amount / 2)?.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between border-t border-slate-200 pt-2 font-bold text-base"><span>Total:</span><span>₹{invoiceModal.total_amount?.toLocaleString('en-IN')}</span></div>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mb-4">
                      <span>Transaction: {invoiceModal.transaction_id?.slice(0, 12)}...</span>
                      <span className={`px-2 py-0.5 rounded capitalize ${invoiceModal.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{invoiceModal.payment_status}</span>
                    </div>
                    <button onClick={() => window.print()} className="w-full py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm" data-testid="print-invoice-btn">Print / Download PDF</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'simulations' && (
            <div data-testid="admin-simulations-section">
              <SimulationStudio />
            </div>
          )}

          {activeTab === 'account' && (
            <div data-testid="account-section" className="max-w-3xl space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-1">Your Account</h3>
                <p className="text-sm text-slate-500 mb-4">Manage the login email and password for this admin account.</p>
                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-500">Current login email</span>
                    <span className="font-medium text-slate-900" data-testid="current-email-display">{currentUser?.email || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Role</span>
                    <span className="font-medium text-slate-900 capitalize">{currentUser?.role || 'admin'}</span>
                  </div>
                </div>
              </div>

              {/* Change Email */}
              <form onSubmit={handleChangeEmail} className="bg-white rounded-xl border border-slate-200 p-6" data-testid="change-email-form">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-1">Change login email</h3>
                <p className="text-sm text-slate-500 mb-4">After updating, you must log in with the new email next time. Password stays the same.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New email address</label>
                    <input
                      type="email"
                      required
                      value={accountEmailForm.new_email}
                      onChange={(e) => setAccountEmailForm({ ...accountEmailForm, new_email: e.target.value })}
                      placeholder="support@thecaseverse.in"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="new-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current password (to confirm)</label>
                    <input
                      type="password"
                      required
                      value={accountEmailForm.current_password}
                      onChange={(e) => setAccountEmailForm({ ...accountEmailForm, current_password: e.target.value })}
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="email-current-password-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-5">
                  <button
                    type="submit"
                    disabled={savingEmail}
                    className="px-5 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50"
                    data-testid="save-email-btn"
                  >
                    {savingEmail ? 'Updating...' : 'Update email'}
                  </button>
                </div>
              </form>

              {/* Change Password */}
              <form onSubmit={handleChangePassword} className="bg-white rounded-xl border border-slate-200 p-6" data-testid="change-password-form">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-1">Change password</h3>
                <p className="text-sm text-slate-500 mb-4">Use a strong password (at least 8 characters, mix letters and numbers).</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current password</label>
                    <input
                      type="password"
                      required
                      value={accountPwdForm.current_password}
                      onChange={(e) => setAccountPwdForm({ ...accountPwdForm, current_password: e.target.value })}
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="pwd-current-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={accountPwdForm.new_password}
                      onChange={(e) => setAccountPwdForm({ ...accountPwdForm, new_password: e.target.value })}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="pwd-new-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={accountPwdForm.confirm_password}
                      onChange={(e) => setAccountPwdForm({ ...accountPwdForm, confirm_password: e.target.value })}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      data-testid="pwd-confirm-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-5">
                  <button
                    type="submit"
                    disabled={savingPwd}
                    className="px-5 py-2 bg-slate-900 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50"
                    data-testid="save-password-btn"
                  >
                    {savingPwd ? 'Updating...' : 'Update password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}